import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { msalInstance } from "../services/authInstance";
import { loginRequest } from "../services/authConfig";
import lamlogo from "../assets/lamlogo.png";
import errorMessages from '../constants/errorMessages';
import { LamBranding, LamColor } from "../lamTheme";
import { fetchGroupIdsFromRfq } from "../services/api";
import { getTabItem, setTabItem } from "../util/tabStorage";


const ALLOWED_GROUP_ID = process.env.REACT_APP_ALLOWED_GROUP_ID ?? '';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [loginError, setLoginError] = useState(false); // Place this at the top of your component
  const [loginerrorMessage, setLoginErrorMessage] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  //const [searchParams] = useSearchParams();


  /**
 * logoutIfUserExists
 * ------------------
 * This function checks if a user account is already signed in.
 * 
 * - If so, it removes any stored RFQ number from session storage.
 * - Then it triggers a logout redirect to force the user to sign in again.
 * - This ensures that if a different user needs to sign in, they don’t get stuck
 *   with a stale session.
 * 
 * The MSAL instance must be initialized before you can query accounts.
 */


  useEffect(() => {
    const checkIfUserExists = async () => {
      await msalInstance.initialize();
      const accounts = msalInstance.getAllAccounts();
      //debugger;

      const url = new URL(window.location.href);
      const rfq = url.searchParams.get("RequestNumber") || "";

      if (accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0]);

        // Persist RFQ for this tab
        if (rfq) {
          setTabItem("rfq", rfq);
        }

        // Check if userContext already exists for this tab
        const existingContext = getTabItem("userContext");

        if (existingContext || rfq) {
          // If we have RFQ or context, update and navigate
          const account = accounts[0];
          const name = account.name || "";
          const email = account.username || "";


          const existingContext = getTabItem<any>("userContext");

          setTabItem("userContext", {
            ...(existingContext || {}), // ✅ safe spread
            name,
            email,
            rfq: rfq || existingContext?.rfq,
          });

          const loginState = rfq ? JSON.stringify({ rfq }) : undefined;


          window.history.replaceState({}, document.title, window.location.origin + window.location.hash);
          navigate("/dashboard");
        }
        // Else: stay on login page so user can click Sign In
      }
    };

    checkIfUserExists();
  }, []);


  /**
 * handleSignIn
 * --------------
 * This function initiates the MSAL login flow using a redirect.
 * 
 * - If an RFQ number is present in the URL query params, it saves it in session storage.
 * - It includes that RFQ number in the MSAL login state so you can retrieve it after redirect.
 * - If the login fails, it checks if an interaction is already in progress,
 *   and sets an appropriate error state and message for the UI.
 */


  const handleSignIn = async () => {
    try {
      await msalInstance.initialize();
      const accounts = msalInstance.getAllAccounts();

      const url = new URL(window.location.href);
      const rfq = url.searchParams.get("RequestNumber") || "";

      // Always store RFQ for this tab
      if (rfq) {
        setTabItem("rfq", rfq);
      }
      //debugger;

      if (accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0]);
        const account = accounts[0];
        const name = account.name || "";
        const email = account.username || "";

        // Merge with existing context instead of overwriting
        const existingContext = getTabItem<any>("userContext") || {};
        setTabItem("userContext", {
          ...existingContext,
          name,
          email,
          rfq: rfq || existingContext.rfq, // ✅ preserve RFQ if URL is empty
        });

        window.history.replaceState({}, document.title, window.location.origin + window.location.hash);
        navigate("/dashboard");
        return;
      }

      const loginState = rfq ? JSON.stringify({ rfq }) : undefined;
      await msalInstance.loginRedirect({
        ...loginRequest,
        ...(loginState && { state: loginState }),
      });
    } catch (error: any) {
      console.error(error);
      if (error.errorCode === "interaction_in_progress") {
        setLoginError(true);
        setLoginErrorMessage(errorMessages.interactionInProgress);
      } else {
        setLoginError(true);
        setLoginErrorMessage(errorMessages.loginfailed);
      }
    }
  };



  /**
    * handleRedirectResponse
    * -----------------------
    * This function handles the redirect response after MSAL loginRedirect.
    * 
    * - Initializes MSAL and resolves the redirect promise.
    * - Extracts the signed-in account, access token, and any state (like RFQ number).
    * - Uses the token to fetch group memberships if needed.
    * - Determines whether the user is a BUYER or SUPPLIER.
    * - Navigates to the dashboard with the appropriate role.
    */
  useEffect(() => {
    const handleRedirectResponse = async () => {
      try {
        await msalInstance.initialize();
        const response = await msalInstance.handleRedirectPromise();
        const account = response?.account ?? msalInstance.getAllAccounts()[0];
        if (!account) return;

        msalInstance.setActiveAccount(account);
        //debugger;
        const existingContext = getTabItem<any>("userContext") || {};

        let accessTokenGraph = response?.accessToken || existingContext.graphToken;
        if (!accessTokenGraph) {
          accessTokenGraph = await refreshGraphToken();
        }

        // ✅ Persist in tab storage
        const ctx = getTabItem<any>("userContext") || {};
        ctx.graphToken = accessTokenGraph;
        setTabItem("userContext", ctx);

        const accessToken = await acquireAccessToken(account);

        const rfqNumber = resolveRfqNumber(response?.state);
        console.log("Resolved RFQ:", rfqNumber);


        const { name, email, isBuyer } = extractUserInfo(account, response);

        if (rfqNumber) {
          await handleRfqNavigation(rfqNumber, isBuyer, {
            name,
            email,
            accessToken,
            accessTokenGraph,
          });
        } else {
          navigateToDashboard(isBuyer ? "BUYER" : "SUPPLIER", {
            name,
            token: accessToken,
            email,
            graphToken: accessTokenGraph,
          });
        }
      } catch (error) {
        console.error("Login redirect error:", error);
      }
    };

    handleRedirectResponse();
  }, []);
  // Acquire token silently
  const acquireAccessToken = async (account: any): Promise<string> => {
    const tokenRequest = {
      scopes: [`api://${process.env.REACT_APP_BACKEND_API_CLIENTID}/user_impersonation`],
      account,
    };
    const tokenResponse = await msalInstance.acquireTokenSilent(tokenRequest);
    const ctx = getTabItem<any>("userContext");
    if (ctx) {
      ctx.token = tokenResponse.accessToken;
      setTabItem("userContext", ctx);

    }
    return tokenResponse.accessToken;
  };

  // Resolve RFQ number from state or session

  const resolveRfqNumber = (state?: string): string | null => {
    if (!state) return getTabItem<string>("rfq") || "";
    try {
      const parsed = JSON.parse(state);
      if (parsed.rfq) {
        setTabItem("rfq", parsed.rfq);
        const existingContext = getTabItem<any>("userContext") || {};
        setTabItem("userContext", { ...existingContext, rfq: parsed.rfq });
        return parsed.rfq;
      }
    } catch (err) {
      console.warn("Failed to parse MSAL state:", err);
    }
    return getTabItem<string>("rfq") || "";
  };


  // Extract user info and role

  const extractUserInfo = (account: any, response: any) => {
    const idTokenClaims: any = response?.idTokenClaims || account?.idTokenClaims || {};
    const userGroups: string[] = idTokenClaims.groups || [];
    const name = account?.name || idTokenClaims?.name || "";
    const email = account?.username || idTokenClaims?.preferred_username || "";
    const isBuyer = userGroups
      .map(group => group.toLowerCase())
      .includes(ALLOWED_GROUP_ID.toLowerCase());

    return { name, email, isBuyer };
  };


  // Handle RFQ-based navigation
  const handleRfqNavigation = async (
    rfqNumber: string,
    isBuyer: boolean,
    { name, email, accessToken, accessTokenGraph }: any
  ) => {
    if (isBuyer) {
      navigateToDashboard("BUYER", {
        name,
        rfq: rfqNumber,
        token: accessToken,
        email,
        graphToken: accessTokenGraph,
      });
      return;
    }
    try {
      const isSupplier = await fetchGroupIdsFromRfq(rfqNumber, accessToken, accessTokenGraph);
      if (isSupplier) {
        navigateToDashboard("SUPPLIER", {
          name,
          rfq: rfqNumber,
          token: accessToken,
          email,
          graphToken: accessTokenGraph,
        });
      } else {
        setLoginError(true);
        setLoginErrorMessage(errorMessages.default);
      }
    } catch (err) {
      console.error("Error fetching RFQ group IDs:", err);
      setLoginError(true);
      setLoginErrorMessage(errorMessages.loginPermission);
    }
  };


  const refreshGraphToken = async () => {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      try {
        const resp = await msalInstance.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
        });

        const ctx = getTabItem<any>("userContext") || {};
        ctx.graphToken = resp.accessToken;
        setTabItem("userContext", ctx);

        return resp.accessToken;
      } catch (err) {
        console.error("Silent token refresh failed:", err);
      }
    }
    return null;
  };


  // Ensure: const navigate = useNavigate();

  const navigateToDashboard = (
    role: 'BUYER' | 'SUPPLIER',
    {
      name,
      token,
      email,
      graphToken,
      rfq,
    }: {
      name: string;
      token: string;
      email: string;
      graphToken?: string;
      rfq?: string | null;
    }
  ) => {


    //debugger;
    // // Persist per-tab context

    const existingContext = getTabItem<any>("userContext") ?? {};

    setTabItem("userContext", {
      role,
      name: name || existingContext.name,
      token: token || existingContext.token,
      email: email || existingContext.email,
      graphToken: graphToken || existingContext.graphToken,
      rfq: rfq || existingContext.rfq,
    });


    // Remove query string from URL
    window.history.replaceState({}, document.title, window.location.origin);

    navigate('/dashboard', {
      state: {
        role,
        name,
        token,
        email,
        ...(graphToken ? { graphToken } : {}),
        ...(rfq ? { rfq } : {}),
      },
    });
  };



  return (
    <div className="min-h-95vh flex items-center justify-center bg-gradient-to-br from-lam-sand to-lam-mint px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 border border-lam-border">

        {/* Logo & Title */}
        <div className="flex flex-col items-center">
          <img
            src={lamlogo}
            alt="Lam Logo"
            className="w-42 h-12 mb-4"
          />
          <h3 className="text-l font-normal text-lam-midnight mb-2 text-center">
            Sign in with your organizational account
          </h3>
        </div>

        {/* Divider */}
        <div className="my-6 relative text-center">

          <span className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-white px-3 text-sm text-lam-slate">

          </span>
        </div>

        {/* Email Input */}
        <div className="mb-4">

          <small className="block text-xs text-lam-slate mb-2">
            E-mail Address
          </small>
          <input
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              const currentAccount = msalInstance.getActiveAccount();
              if (currentAccount) {
                msalInstance.logoutRedirect();
              }
            }}
            style={{

              borderRadius: `${LamBranding.ControlBorderRadius}px`,
              color: LamColor.Midnight,
            }}
            className="w-full h-[36px] px-3 text-[13px] text-lam-controlText border border-lam-border focus:outline-none focus:ring-2 focus:ring-lam-hoverBorder"
            placeholder="user@yourorganization.com"
          />


          {/* Login Error */}
          {loginError && (
            <div className="mt-4 text-sm text-red-600 bg-red-100 border border-red-300 px-4 py-2 rounded-md">
              {loginerrorMessage}
            </div>
          )}
        </div>

        {/* Sign In Button */}
        <button
          onClick={handleSignIn}
          className="w-full h-[44px] px-4 bg-lam-darkGreen text-white  font-normal rounded-full hover:bg-[#145C49] transition duration-200"
        >
          Sign in
        </button>


      </div>
    </div>
  );

};

export default Login;
